import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiCalendar, FiUser } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';
import { ExamsResolutionAnswerKey } from '../../exams-resolution/exams-resolution-answer-key';

const BADGE_CLASSNAME = 'rounded-full px-2.5 py-1 text-xs font-bold';

// Mesmo dicionário de status de ExamMetricsUserListItem.jsx (Default), só com
// os variants de badge do tema playful (*-soft) no lugar dos do Default.
const STATUS_BADGE_META = {
  waiting_correction: { label: 'Aguardando correção', variant: 'amber-soft' },
  'waiting-correction': { label: 'Aguardando correção', variant: 'amber-soft' },
  reviews: { label: 'Em correção', variant: 'amber-soft' },
  review: { label: 'Em correção', variant: 'amber-soft' },
  approved: { label: 'Aprovado', variant: 'success-soft' },
  disapproved: { label: 'Reprovado', variant: 'danger-soft' },
  accepting_answers: { label: 'Aceitando respostas', variant: 'muted' },
  corrected: { label: 'Corrigido', variant: 'success-soft' }
};

function getStatusMeta(status) {
  return STATUS_BADGE_META[status] || { label: '--', variant: 'muted' };
}

// Mesmo esqueleto visual de ExamRowTeacher (Provas) — lista vertical com o
// mesmo shell (borda + shadow-card), badges ao lado do nome e metadados com
// ícone abaixo. Gabarito reaproveita a mesma modal do Default
// (ExamsResolutionAnswerKey); Corrigir dissertativa navega pra mesma tela
// Default de sempre (sem versão playful), igual às outras ações desta tela.
export function ExamResultRowTeacher({ examStudent, exam, index = 0 }) {
  const navigate = useNavigate();
  const [showAnswerKey, setShowAnswerKey] = useState(false);

  const attempts = examStudent?.user?.['question-books-users'] || [];
  // Mesma regra de ExamMetricsUserListItem.jsx: último envio do aluno pela
  // data de finalização.
  const lastAttempt = [...attempts].sort((a, b) => new Date(b?.['finished-at']) - new Date(a?.['finished-at']))[0];

  // Mesma regra de ExamMetricsUserListItem.jsx: visível com permissão +
  // prova com questão discursiva, independente do status de correção — só o
  // texto do botão alterna entre "Corrigir discursiva" e "Acessar correção".
  const canCorrectDiscursive = usePermission('Corrigir questões discursivas', 'Provas e simulados') && exam?.['has-discursive'];
  const isWaitingCorrection = lastAttempt?.['correction-status'] === 'waiting_correction' || lastAttempt?.['correction-status'] === 'review';

  const correctionMeta = getStatusMeta(lastAttempt?.['correction-status']);
  const approvalMeta = getStatusMeta(lastAttempt?.status);
  // Mesma regra de AdminMetrics/ExamMetricsUserListItem: simulado
  // (visibility === 'open') não mostra status de aprovação, só de correção.
  const showApprovalBadge = exam?.visibility !== 'open';

  const score = parseFloat(lastAttempt?.score);
  const scoreLabel = Number.isNaN(score) ? '--' : score.toFixed(2);

  const resolvedAt = lastAttempt?.['finished-at'] ? moment(lastAttempt['finished-at']).format('DD/MM/YYYY') : null;
  // Mesma regra de ExamMetricsUserListItem.jsx: simulado é corrigido pela
  // plataforma; sem essa informação (nem "corrected-by" nem simulado), o
  // metadado não aparece.
  const correctedBy = exam?.visibility === 'open' ? 'Plataforma' : lastAttempt?.['corrected-by'];

  const meta = [resolvedAt ? { icon: FiCalendar, text: `Resolvido em ${resolvedAt}` } : null, correctedBy ? { icon: FiUser, text: `Corrigido por ${correctedBy}` } : null].filter(Boolean);

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
        style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
        data-testid="exam-result-row-teacher"
      >
        <div className="flex min-w-[200px] flex-1 items-start gap-3">
          <AvatarWrapper
            src={examStudent?.user?.image?.url}
            text={examStudent?.user?.name}
            className="size-9"
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className="mb-0 text-base font-extrabold leading-snug"
                style={{ color: '#222731' }}
              >
                {examStudent?.user?.name}
              </h3>

              <Badge
                variant={correctionMeta.variant}
                className={`shrink-0 ${BADGE_CLASSNAME}`}
              >
                {correctionMeta.label}
              </Badge>

              {showApprovalBadge && (
                <Badge
                  variant={approvalMeta.variant}
                  className={`shrink-0 ${BADGE_CLASSNAME}`}
                >
                  {approvalMeta.label}
                </Badge>
              )}
            </div>

            {meta.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-neutral-500">
                {meta.map((item, metaIndex) => (
                  <span
                    key={metaIndex}
                    className="flex items-center gap-1.5"
                  >
                    <item.icon className="h-3.5 w-3.5 text-mission" />
                    {item.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <span className="text-sm font-bold text-neutral-500">Nota {scoreLabel}</span>

          {canCorrectDiscursive && (
            <PlayfulCardButton
              className="h-9 w-auto px-6"
              onClick={() => navigate(`/correcao-discursiva/${lastAttempt?.['question-book-id']}/${examStudent?.id}/${lastAttempt?.id}`)}
            >
              {isWaitingCorrection ? 'Corrigir discursiva' : 'Acessar correção'}
            </PlayfulCardButton>
          )}

          <PlayfulCardButton
            variant="outline"
            className="h-9 w-auto px-6"
            onClick={() => setShowAnswerKey(true)}
          >
            Gabarito
          </PlayfulCardButton>
        </div>
      </div>

      <Dialog
        open={showAnswerKey}
        onOpenChange={setShowAnswerKey}
      >
        <DialogContent size="md">
          <ExamsResolutionAnswerKey
            examId={exam?.id}
            examData={attempts}
            amountQuestions={exam?.['question-count']}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
